import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Lightbulb, ArrowRight, X } from 'lucide-react';

export default function ExamWishModal({ isPremium, userId }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    // Tied specifically to the user ID so every user sees it once upon logging in
    const storageKey = `omniclarix_exam_wish_${userId}`;
    const hasSeenModal = localStorage.getItem(storageKey);

    if (!hasSeenModal) {
      setIsOpen(true);
    }
  }, [userId]);

  const handleClose = () => {
    if (userId) {
      localStorage.setItem(`omniclarix_exam_wish_${userId}`, 'true');
    }
    setIsOpen(false);
  };

  const handleUpgrade = () => {
    handleClose();
    navigate('/pricing');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 text-center animate-fade-in">
        
        {/* Close Icon Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header Icon */}
        <div className="mx-auto w-12 h-12 rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
          <GraduationCap className="w-6 h-6" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          We Wish You All the Best!
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Exam season can be demanding, but we know you have put in the effort. OmniClarix is here to support your study sessions every step of the way.
        </p>

        {!isPremium && (
          <div className="bg-indigo-50 dark:bg-indigo-950/40 p-3.5 rounded-xl mb-5 text-left border border-indigo-100 dark:border-indigo-900/60 flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">
                Studying multiple subjects?
              </p>
              <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">
                Upgrade to Premium for full access to unlimited textbook summaries and study notes.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {!isPremium && (
            <button
              onClick={handleUpgrade}
              className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl text-sm transition-all shadow-md shadow-indigo-200 dark:shadow-none flex items-center justify-center gap-2"
            >
              <span>Get Full Access</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleClose}
            className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl text-sm transition-all"
          >
            {isPremium ? 'Continue to Dashboard' : 'Maybe Later'}
          </button>
        </div>
      </div>
    </div>
  );
}